import React, { useEffect, useState } from "react";
import "./style.css";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { FaClipboardCheck, FaPencilAlt, FaTrash, FaPlus, FaToggleOn, FaToggleOff } from "react-icons/fa";
import API_HOST, { HEADERS_JSON, METHOD_POST } from "../../../ApiConfig";
import { businessThemes } from "../../../theme/businessThemes";
import { Modal, Button, Form } from "react-bootstrap";

// ── Helpers módulo ───────────────────────────────────────────────────────────
const buildCategoriasColumns = (
  handleChangeStatus: (id: number, status: string) => void,
  handleDelete: (id: number, desc: string) => void,
  setModalMode: any, setCategoryEdit: any, setShowModal: any
) => [
  { name: "ID", selector: (row: any) => row.id, sortable: true, width: "70px" },
  { name: "Descripción", selector: (row: any) => row.description, sortable: true, grow: 3 },
  {
    name: "Estado", sortable: true, width: "130px",
    cell: (row: any) => (
      <span
        className={`badge ${row.status === "activo" ? "bg-success" : "bg-secondary"}`}
        style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "12px" }}
      >
        {row.status === "activo" ? "Activo" : "Inactivo"}
      </span>
    ),
  },
  {
    name: "Acciones", ignoreRowClick: true, allowOverflow: true, button: true, width: "140px",
    cell: (row: any) => (
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        {/* Editar */}
        <button
          className="btn btn-sm p-0"
          title="Editar descripción"
          onClick={() => { setModalMode("edit"); setCategoryEdit({ id: row.id, description: row.description }); setShowModal(true); }}
          style={{ background: "none", border: "none" }}
        >
          <FaPencilAlt
            color="white"
            className="bg-warning rounded-circle p-1"
            style={{ fontSize: "28px" }}
          />
        </button>
        {/* Activar / Inactivar */}
        <button
          className="btn btn-sm p-0"
          title={row.status === "activo" ? "Inactivar" : "Activar"}
          onClick={() => handleChangeStatus(row.id, row.status)}
          style={{ background: "none", border: "none" }}
        >
          {row.status === "activo"
            ? <FaToggleOn color="white" className="bg-success rounded-circle p-1" style={{ fontSize: "28px" }} />
            : <FaToggleOff color="white" className="bg-secondary rounded-circle p-1" style={{ fontSize: "28px" }} />
          }
        </button>
        {/* Eliminar */}
        <button
          className="btn btn-sm p-0"
          title="Eliminar categoría"
          onClick={() => handleDelete(row.id, row.description)}
          style={{ background: "none", border: "none" }}
        >
          <FaTrash
            color="white"
            className="bg-danger rounded-circle p-1"
            style={{ fontSize: "28px" }}
          />
        </button>
      </div>
    ),
  },
];

const buildCategoryBody = (modalMode: string, categoryEdit: any, selectedGroup: string): object =>
  modalMode === "add"
    ? { valid: "newCategory", description: categoryEdit.description, idbusiness_group: selectedGroup }
    : { valid: "updateCategory", id: categoryEdit.id, description: categoryEdit.description, idbusiness_group: selectedGroup };

const catFetchBusinessGroups = async (setBusinessGroups: any, setSelectedGroup: any) => {
  try {
    const res = await fetch(`${API_HOST}`, { method: METHOD_POST, headers: HEADERS_JSON,
      body: JSON.stringify({ valid: "getSelectCompany" }) });
    const data = JSON.parse(await res.text());
    if (data?.payload) {
      setBusinessGroups(data.payload);
      if (data.payload.length > 0) setSelectedGroup(data.payload[0].idbusiness_group);
    }
  } catch { setBusinessGroups([]); }
};


const CategoriasEvaluacionesPage = () => {
  const { idbusiness_group } = useSelector((state: any) => state.emp);
  const theme = businessThemes[idbusiness_group || 1];
  const [categories, setCategories] = useState<any[]>([]);
  const [businessGroups, setBusinessGroups] = useState<any[]>([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [loading, setLoading] = useState(false);

  // Modal crear/editar
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add"|"edit">("add");
  const [categoryEdit, setCategoryEdit] = useState<any>({ id: null, description: "" });

  // Modal confirmación (reemplaza window.confirm)
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  useEffect(() => { fetchBusinessGroups(); }, []);
  useEffect(() => { if (selectedGroup) fetchCategories(selectedGroup); }, [selectedGroup]);

  const fetchBusinessGroups = () => catFetchBusinessGroups(setBusinessGroups, setSelectedGroup);

  const fetchCategories = async (groupId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_HOST}`, {
        method: METHOD_POST, headers: HEADERS_JSON,
        body: JSON.stringify({ valid: "selectCategoriesByGroup", idbusiness_group: groupId })
      });
      const data = JSON.parse(await res.text());
      setCategories(data.payload || []);
    } catch { setCategories([]); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!categoryEdit.description) return;
    setLoading(true);
    await fetch(`${API_HOST}`, {
      method: METHOD_POST, headers: HEADERS_JSON,
      body: JSON.stringify(buildCategoryBody(modalMode, categoryEdit, selectedGroup))
    });
    setShowModal(false);
    setCategoryEdit({ id: null, description: "" });
    fetchCategories(selectedGroup);
    setLoading(false);
  };

  // ── Pide confirmación y ejecuta la acción al confirmar ───────────────────
  const askConfirm = (msg: string, action: () => void) => {
    setConfirmMsg(msg);
    setConfirmAction(() => action);
    setShowConfirm(true);
  };

  const handleChangeStatus = (id: number, currentStatus: string) => {
    // currentStatus llega como "activo" o "inactivo" (ya normalizado en backend)
    const newStatus = currentStatus === "activo" ? "inactivo" : "activo";
    const label = newStatus === "activo" ? "activar" : "inactivar";
    askConfirm(
      `¿Seguro que deseas ${label} esta categoría?`,
      async () => {
        setLoading(true);
        await fetch(`${API_HOST}`, {
          method: METHOD_POST, headers: HEADERS_JSON,
          body: JSON.stringify({ valid: "changeCategoryStatus", id, status: newStatus })
        });
        fetchCategories(selectedGroup);
        setLoading(false);
      }
    );
  };

  const handleDelete = (id: number, description: string) => {
    askConfirm(
      `¿Seguro que deseas eliminar permanentemente la categoría "${description}"? Esta acción no se puede deshacer.`,
      async () => {
        setLoading(true);
        await fetch(`${API_HOST}`, {
          method: METHOD_POST, headers: HEADERS_JSON,
          body: JSON.stringify({ valid: "deleteCategory", id })
        });
        fetchCategories(selectedGroup);
        setLoading(false);
      }
    );
  };

  const columns = buildCategoriasColumns(handleChangeStatus, handleDelete, setModalMode, setCategoryEdit, setShowModal);

  const pagOptions = {
    rowsPerPageText: "Filas por páginas",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: "Todos",
  };

  const conditionalRowStyles = [
    {
      when: (row: any) => row.status === "inactivo",
      style: { backgroundColor: "#f0f0f0", color: "#999" },
    },
  ];

  return (
    <div className="container mt-4">
      <h3 className="mb-3">
        <FaClipboardCheck /> Categorías de Evaluaciones
      </h3>

      <div className="mb-3 d-flex align-items-end gap-3">
        <div style={{ flex: 1 }}>
          <label htmlFor="businessGroupSelect" className="form-label">
            Grupo de Negocio:
          </label>
          <select
            id="businessGroupSelect"
            className="form-select"
            value={selectedGroup}
            onChange={e => setSelectedGroup(e.target.value)}
          >
            {Array.isArray(businessGroups) && businessGroups.length > 0 ? (
              businessGroups.map((group: any) => (
                <option key={group.idbusiness_group} value={group.idbusiness_group}>
                  {group.name}
                </option>
              ))
            ) : (
              <option value="">No hay grupos</option>
            )}
          </select>
        </div>
        <Button variant="secondary" onClick={() => window.history.back()}>
          Regresar
        </Button>
        <Button variant="success" onClick={() => { setModalMode("add"); setCategoryEdit({ id: null, description: "" }); setShowModal(true); }}>
          <FaPlus /> Nueva Categoría
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={categories}
        progressPending={loading}
        pagination
        highlightOnHover
        paginationComponentOptions={pagOptions}
        conditionalRowStyles={conditionalRowStyles}
        defaultSortFieldId={1}
        noDataComponent={<div className="p-4 text-muted">No hay categorías para este grupo</div>}
        progressComponent={<div className="p-4">Cargando categorías...</div>}
      />

      {/* ── Modal crear / editar ─────────────────────────────────────────── */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === "add" ? "Nueva Categoría" : "Editar Categoría"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                type="text"
                value={categoryEdit.description}
                onChange={e => setCategoryEdit({ ...categoryEdit, description: e.target.value })}
                autoFocus
                onKeyDown={e => { if (e.key === "Enter" && categoryEdit.description) handleSave(); }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={!categoryEdit.description || loading}>
            {loading ? "Guardando..." : "Guardar"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal confirmación (reemplaza window.confirm) ────────────────── */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "16px" }}>Confirmar acción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ margin: 0 }}>{confirmMsg}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Cancelar</Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowConfirm(false);
              if (confirmAction) confirmAction();
            }}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CategoriasEvaluacionesPage;